import { supabase } from '@/integrations/supabase/client';
import { UserRole } from '@/types/userRole';
import { logger } from '@/utils/logger';

export type TicketPriority = 'low' | 'medium' | 'high';

export interface SupportTicket {
  id: string;
  caseNumber: string;
  subject: string;
  message: string;
  content?: string;
  status: 'open' | 'closed' | 'pending' | 'resolved';
  priority: TicketPriority;
  userId: string;
  userName: string;
  userEmail: string;
  userRole: string;
  createdAt: Date;
  updatedAt: Date;
  instanceId?: string;
  replies?: TicketReply[];
}

export interface TicketReply {
  id: string;
  content: string;
  userId: string;
  userName: string;
  userRole: string;
  userEmail: string;
  createdAt: Date;
}

export interface TicketFormData {
  subject: string;
  priority: TicketPriority;
  content: string;
  instanceId?: string;
}

const isStaff = (role: UserRole | string) =>
  ['admin', 'superadmin', 'masterAdmin'].includes(String(role));

const mapTicket = (
  row: any,
  authorProfiles: Map<string, any>,
  messages: any[] = [],
): SupportTicket => {
  const author = authorProfiles.get(row.user_id) ?? {};
  return {
    id: row.id,
    caseNumber: `SUP-${row.id.slice(0, 8).toUpperCase()}`,
    subject: row.subject,
    message: row.description ?? '',
    content: row.description ?? '',
    status: row.status,
    priority: row.priority,
    userId: row.user_id,
    userName:
      [author.first_name, author.last_name].filter(Boolean).join(' ') ||
      author.email ||
      'User',
    userEmail: author.email ?? '',
    userRole: author.role ?? 'user',
    createdAt: new Date(row.created_at),
    updatedAt: new Date(row.updated_at),
    replies: messages
      .filter((m) => m.ticket_id === row.id)
      .map((m) => {
        const a = authorProfiles.get(m.sender_user_id) ?? {};
        return {
          id: m.id,
          content: m.body,
          userId: m.sender_user_id,
          userName:
            [a.first_name, a.last_name].filter(Boolean).join(' ') || a.email || 'User',
          userEmail: a.email ?? '',
          userRole: a.role ?? 'user',
          createdAt: new Date(m.created_at),
        };
      }),
  };
};

const loadProfiles = async (ids: string[]) => {
  const unique = Array.from(new Set(ids.filter(Boolean)));
  if (unique.length === 0) return new Map<string, any>();
  const { data } = await supabase
    .from('profiles')
    .select('id, email, first_name, last_name, role')
    .in('id', unique);
  const map = new Map<string, any>();
  (data ?? []).forEach((p) => map.set(p.id, p));
  return map;
};

export const getTickets = async (userRole: UserRole | string): Promise<SupportTicket[]> => {
  try {
    let query = supabase.from('support_tickets').select('*').order('updated_at', { ascending: false });
    const { data: user } = await supabase.auth.getUser();
    if (!isStaff(userRole) && user?.user) {
      query = query.eq('user_id', user.user.id);
    }
    const { data: rows, error } = await query;
    if (error) throw error;
    const ticketIds = (rows ?? []).map((r) => r.id);
    const { data: messages } = ticketIds.length
      ? await supabase.from('ticket_messages').select('*').in('ticket_id', ticketIds)
      : { data: [] as any[] };
    const authorIds = [
      ...(rows ?? []).map((r: any) => r.user_id),
      ...((messages ?? []).map((m: any) => m.sender_user_id)),
    ];
    const profiles = await loadProfiles(authorIds);
    return (rows ?? []).map((r) => mapTicket(r, profiles, messages ?? []));
  } catch (e) {
    logger.error('getTickets failed', e);
    return [];
  }
};

export const getTicket = async (ticketId: string): Promise<SupportTicket | null> => {
  const { data: row, error } = await supabase
    .from('support_tickets')
    .select('*')
    .eq('id', ticketId)
    .maybeSingle();
  if (error || !row) return null;
  const { data: messages } = await supabase
    .from('ticket_messages')
    .select('*')
    .eq('ticket_id', ticketId)
    .order('created_at', { ascending: true });
  const profiles = await loadProfiles([
    row.user_id,
    ...((messages ?? []).map((m: any) => m.sender_user_id)),
  ]);
  return mapTicket(row, profiles, messages ?? []);
};

export const getSupportMessageLevel = (
  userRole: UserRole | string,
): 'user' | 'admin' | 'system' => {
  if (isStaff(userRole)) return 'admin';
  if (userRole === 'system') return 'system';
  return 'user';
};

export const createTicket = async (
  ticketData: TicketFormData,
  userRole: UserRole | string,
  userId?: string,
  _userName?: string,
  _userEmail?: string,
): Promise<SupportTicket> => {
  const { data: authData } = await supabase.auth.getUser();
  const uid = userId ?? authData?.user?.id;
  if (!uid) throw new Error('Not authenticated');
  const { data, error } = await supabase
    .from('support_tickets')
    .insert({
      user_id: uid,
      subject: ticketData.subject,
      description: ticketData.content,
      priority: ticketData.priority,
      status: 'open',
    })
    .select('*')
    .single();
  if (error) throw error;
  const profiles = await loadProfiles([uid]);
  return mapTicket(data, profiles);
};

const updateStatus = async (
  ticketId: string,
  status: 'open' | 'closed' | 'pending' | 'resolved',
) => {
  const { data, error } = await supabase
    .from('support_tickets')
    .update({ status })
    .eq('id', ticketId)
    .select('*')
    .single();
  if (error) throw error;
  const profiles = await loadProfiles([data.user_id]);
  return mapTicket(data, profiles);
};

export const closeTicket = (ticketId: string) => updateStatus(ticketId, 'closed');
export const resolveTicket = (ticketId: string) => updateStatus(ticketId, 'resolved');
export const reopenTicket = (ticketId: string) => updateStatus(ticketId, 'open');

export const addReplyToTicket = async (
  ticketId: string,
  reply: TicketReply,
): Promise<SupportTicket> => {
  const { data: authData } = await supabase.auth.getUser();
  const uid = reply.userId || authData?.user?.id;
  if (!uid) throw new Error('Not authenticated');
  const { error } = await supabase.from('ticket_messages').insert({
    ticket_id: ticketId,
    sender_user_id: uid,
    body: reply.content,
    is_internal: false,
  });
  if (error) throw error;
  await supabase.from('support_tickets').update({ status: 'pending' }).eq('id', ticketId);
  const t = await getTicket(ticketId);
  if (!t) throw new Error('Ticket not found');
  return t;
};

export const getUserTickets = getTickets;
export const replyToTicket = addReplyToTicket;

export const getTicketPermissions = (userRole: UserRole | string) => ({
  canResolve: isStaff(userRole),
  canClose: isStaff(userRole),
  canReopen: isStaff(userRole),
  canManage: isStaff(userRole),
});

export const formatCentralTime = (date: Date): string => {
  return new Date(date).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'America/Chicago',
  });
};
